import { useEffect, useState } from "react";

// Waits before each automatic retry while the tab is in view.
const RETRY_DELAYS_MS = [1000, 3000, 10000];
// Coming back to the tab or back online retries too; this caps all tries for one picture.
const MAX_ATTEMPTS = 6;

/** The link with a `retry` marker, so the browser fetches it fresh instead of reusing the failed copy. */
export const withRetryMarker = (src, attempt) => {
  if (!src || !attempt || src.startsWith("blob:") || src.startsWith("data:")) return src;
  return `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}`;
};

/**
 * An <img> that tries again when it fails to load: after 1, 3 and 10 seconds
 * while the tab is in view, and again whenever the tab comes back into view or
 * the device comes back online (6 tries at most). A phone that wakes up with
 * its connection still coming back can fail a download, and our image links
 * stay the same for an hour, so a plain <img> (even remounted on another step)
 * never asked again and stayed blank until a full refresh.
 *
 * The retry adds `retry=N` to the link. Signed image links only sign the id
 * and expiry, so the extra parameter doesn't break them.
 */
const RetryImage = ({ src, alt = "", onError, ...props }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  useEffect(() => {
    if (!failed || attempt >= MAX_ATTEMPTS) return undefined;
    const retry = () => {
      setFailed(false);
      setAttempt((n) => n + 1);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") retry();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    // The connection is back: no need to wait for the next timed try.
    window.addEventListener("online", onVisible);
    const timer =
      document.visibilityState !== "hidden" && attempt < RETRY_DELAYS_MS.length
        ? setTimeout(retry, RETRY_DELAYS_MS[attempt])
        : null;
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("online", onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [failed, attempt]);

  return (
    <img
      {...props}
      alt={alt}
      src={withRetryMarker(src, attempt)}
      onError={(event) => {
        setFailed(true);
        onError?.(event);
      }}
    />
  );
};

export default RetryImage;
